import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Container,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { FiDownload, FiRefreshCw } from "react-icons/fi";
import { format } from "date-fns";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getAssetMovementReport } from "../../services/ReportServices";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import "react-datepicker/dist/react-datepicker.css";
import { BiSolidReport } from "react-icons/bi";
import {
  searchcategoryOptionApi,
  SearchLocationApi,
} from "../../services/SearchService";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div className="text-danger">Component failed to load</div>;
    }
    return this.props.children;
  }
}

// Period options
const periodOptions = [
  { label: "All", value: "all" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "7_days" },
  { label: "Last 15 Days", value: "15_days" },
  { label: "Last 30 Days", value: "30_days" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "3_months" },
  { label: "Custom", value: "custom" },
];

const AssetMovementReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[4]); // Default to "Last 30 Days"
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedFromLocation, setSelectedFromLocation] = useState(null);
  const [selectedToLocation, setSelectedToLocation] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const [filters, setFilters] = useState({
    from_location: "",
    to_location: "",
    category_id: "",
    asset_number: "",
    date_range: "30_days",
    start_date: "",
    end_date: "",
    page: 1,
    page_size: 10,
  });

  // Fetch locations and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingLocations(true);
        setIsLoadingCategories(true);

        const [locationsRes, categoriesRes] = await Promise.all([
          SearchLocationApi(),
          searchcategoryOptionApi(),
        ]);

        setLocationOptions(
          (locationsRes?.data?.results || []).map((loc) => ({
            label: loc.label || "Unnamed Location",
            value: loc.value,
          }))
        );

        setCategoryOptions(
          (categoriesRes?.data?.results || []).map((item) => ({
            label: item.name || "Unnamed Category",
            value: item.id,
          }))
        );
      } catch (error) {
        console.error("Failed to load options:", error);
        toast.error("Failed to load dropdown options");
      } finally {
        setIsLoadingLocations(false);
        setIsLoadingCategories(false);
      }
    };

    fetchData();
  }, []);

  // Handle period changes
  useEffect(() => {
    const today = new Date();
    let start_date = "";
    let end_date = "";

    if (!selectedPeriod || selectedPeriod.value === "all") {
      setFilters((prev) => ({
        ...prev,
        date_range: "all",
        start_date: "",
        end_date: "",
        page: 1,
      }));
      return;
    }

    switch (selectedPeriod.value) {
      case "yesterday":
        start_date = end_date = format(
          new Date(today.setDate(today.getDate() - 1)),
          "yyyy-MM-dd"
        );
        break;
      case "7_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 7)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "15_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 15)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "30_days":
        start_date = format(
          new Date(today.setDate(today.getDate() - 30)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      case "last_month":
        const firstOfThisMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );
        const lastDayOfLastMonth = new Date(firstOfThisMonth - 1);
        const firstDayOfLastMonth = new Date(
          lastDayOfLastMonth.getFullYear(),
          lastDayOfLastMonth.getMonth(),
          1
        );
        start_date = format(firstDayOfLastMonth, "yyyy-MM-dd");
        end_date = format(lastDayOfLastMonth, "yyyy-MM-dd");
        break;
      case "3_months":
        start_date = format(
          new Date(today.setDate(today.getDate() - 90)),
          "yyyy-MM-dd"
        );
        end_date = format(new Date(), "yyyy-MM-dd");
        break;
      default:
        break;
    }

    if (selectedPeriod.value !== "custom") {
      setFilters((prev) => ({
        ...prev,
        date_range: selectedPeriod.value,
        start_date,
        end_date,
        page: 1,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        date_range: "custom",
        start_date: "",
        end_date: "",
        page: 1,
      }));
    }
  }, [selectedPeriod]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  // Fetch report data with from/to location filters
  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        from_location: filters.from_location || undefined,
        to_location: filters.to_location || undefined,
        category_id: filters.category_id || undefined,
        asset_number: filters.asset_number || undefined,
        date_range: filters.date_range,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page: filters.page,
        page_size: filters.page_size,
      };

      const response = await getAssetMovementReport(params);
      if (response?.data?.results) {
        setReportData(
          response.data.results.map((item) => ({
            ...item,
            from_location: item.from_location || "N/A",
            to_location: item.to_location || "N/A",
            current_location: item.current_location || "N/A",
          }))
        );
        setTotalCount(response.data.count || 0);
      }
    } catch (err) {
      console.error("Report fetch error:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to load report"
      );
    } finally {
      setLoading(false);
    }
  };

  // Debounced data fetching
  useEffect(() => {
    const timer = setTimeout(fetchReportData, 300);
    return () => clearTimeout(timer);
  }, [
    filters.page,
    filters.page_size,
    filters.asset_number,
    filters.from_location,
    filters.to_location,
    filters.category_id,
    filters.date_range,
    filters.start_date,
    filters.end_date,
  ]);

  // Handle export
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const exportParams = {
        ...filters,
        export: true,
      };

      const response = await getAssetMovementReport(exportParams, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "asset_movement_report.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Export started successfully");
    } catch (error) {
      toast.error("Export failed. Please try again.");
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (selectedPage) => {
    setFilters((prev) => ({ ...prev, page: selectedPage.selected + 1 }));
    window.scrollTo(0, 0);
  };

  // Safely get location color
  const getLocationColor = (location) => {
    if (!location) return "secondary";
    try {
      const locationLower = location.toLowerCase();
      const colors = {
        warehouse: "primary",
        office: "success",
        site: "info",
      };
      return colors[locationLower] || "secondary";
    } catch (e) {
      console.error("Error determining location color:", e);
      return "secondary";
    }
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="text-primary mb-0">Asset Movement Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Asset Movement Report</li>
          </ol>
        </div>

        {/* Right-side controls */}
        <div className="d-flex align-items-center gap-2 ms-auto">
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading || reportData.length === 0}
            style={{ height: "42px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card style={{minHeight:"350px"}}>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 text-primary fw-bold fs-5 mb-0">
            <BiSolidReport size={22} color="#255d5c" />
            <h5 className="mb-0">Asset Movement Report</h5>
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={filters.asset_number}
              handleSearchChange={(value) =>
                handleFilterChange("asset_number", value)
              }
              placeholder="Search asset number..."
              debounceTime={500}
            />

            {/* From Location Dropdown */}
            <ErrorBoundary>
              <GlobalSearchableDropdown
                options={locationOptions}
                selected={selectedFromLocation}
                onSelect={(item) => {
                  setSelectedFromLocation(item || null);
                  handleFilterChange("from_location", item?.value || "");
                }}
                placeholder="From Location"
                isLoading={isLoadingLocations}
                isDisabled={isLoadingLocations}
              />
            </ErrorBoundary>

            {/* To Location Dropdown */}
            <ErrorBoundary>
              <GlobalSearchableDropdown
                options={locationOptions}
                selected={selectedToLocation}
                onSelect={(item) => {
                  setSelectedToLocation(item || null);
                  handleFilterChange("to_location", item?.value || "");
                }}
                placeholder="To Location"
                isLoading={isLoadingLocations}
                isDisabled={isLoadingLocations}
              />
            </ErrorBoundary>

            {/* Category Dropdown */}
            <ErrorBoundary>
              <GlobalSearchableDropdown
                options={categoryOptions}
                selected={selectedCategory}
                onSelect={(item) => {
                  setSelectedCategory(item || null);
                  handleFilterChange("category_id", item?.value || "");
                }}
                placeholder="Category"
                isLoading={isLoadingCategories}
                isDisabled={isLoadingCategories}
              />
            </ErrorBoundary>

            {/* Period Dropdown */}
            <ErrorBoundary>
              <GlobalSearchableDropdown
                options={periodOptions}
                selected={selectedPeriod}
                onSelect={setSelectedPeriod}
                placeholder="Period"
                width="150px"
              />
            </ErrorBoundary>

            {selectedPeriod?.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  endDate={filters.end_date ? new Date(filters.end_date) : null}
                  onChange={([start, end]) => {
                    setFilters((prev) => ({
                      ...prev,
                      start_date: start ? format(start, "yyyy-MM-dd") : "",
                      end_date: end ? format(end, "yyyy-MM-dd") : "",
                      date_range: "custom",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}
              <Button
                variant="link"
                size="sm"
                onClick={fetchReportData}
                className="p-0 ms-2"
              >
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : (
            <Row>
              <Col lg={12}>
                <Card className="h-100">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Asset Number</th>
                            <th>Asset Name</th>
                            <th>Category</th>
                            <th>From Location</th>
                            <th>To Location</th>
                            <th>Requested By</th>
                            <th>Request Date</th>
                            <th>Current Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="text-center py-4">
                                No data available
                              </td>
                            </tr>
                          ) : (
                            reportData.map((item, index) => (
                              <tr key={`${item.asset_number}-${index}`}>
                                <td>{item.asset_number || "-"}</td>
                                <td>{item.asset_name || "-"}</td>
                                <td>{item.category || "-"}</td>
                                <td>
                                  <Badge
                                    bg={getLocationColor(item.from_location)}
                                  >
                                    {item.from_location || "-"}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge
                                    bg={getLocationColor(item.to_location)}
                                  >
                                    {item.to_location || "-"}
                                  </Badge>
                                </td>
                                <td>{item.requested_by || "-"}</td>
                                <td>{item.request_date || "-"}</td>
                                <td>
                                  <Badge
                                    bg={getLocationColor(item.current_location)}
                                  >
                                    {item.current_location || "-"}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                    {totalCount > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                        <div>
                          Showing {(filters.page - 1) * filters.page_size + 1}{" "}
                          to{" "}
                          {Math.min(
                            filters.page * filters.page_size,
                            totalCount
                          )}{" "}
                          of {totalCount} entries
                        </div>
                        <Pagination
                          currentPage={filters.page - 0}
                          recordsPage={filters.page_size}
                          dataLength={totalCount}
                          handlePagination={handlePageChange}
                          showPageInfo={false}
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AssetMovementReport;
